import { globals } from "@client/globals";
import { generateId } from "@client/lib/random";
import { addVersion } from "@client/lib/settings";
import { EntityType, type GenericMap, type ModuleConfig, type ModuleEntity, type SettingDefinition, Status } from "@client/types";
import { merge } from "lodash";
import * as modules from "./modules";
import { getStructureDefaultValue, type SettingsStructure } from "./structure";

export interface ModuleSettingStructure {
  commonStructure?: SettingsStructure;
  definitions: GenericMap<SettingDefinition> | null;
  settingsStructure: SettingsStructure;
  itemsStructure?: GenericMap<SettingsStructure> | null;
}

export type ModuleSettingStructureEx = ModuleSettingStructure & {
  config: ModuleConfig;
};

export const getSettingsStructure = (moduleType: string): ModuleSettingStructureEx => {
  const { config, definitions, structure } = modules[moduleType];

  const items: GenericMap<SettingsStructure> = {};
  if (config.item) {
    items["default"] = config.item;
  }
  if (config.items) {
    Object.keys(config.items).reduce((res, x) => {
      res[x] = config.items![x];
      return res;
    }, items);
  }

  return {
    config,
    definitions: definitions ?? null,
    settingsStructure: config.layout ?? {},
    itemsStructure: items,
    commonStructure: structure
  };
};

export const getModuleDefaultSettings = (type: string) => {
  const data = getSettingsStructure(type);
  return addVersion(
    getStructureDefaultValue(`module-${type}`, data.settingsStructure, data.definitions, data.commonStructure)
  );
};

export const getModuleItemDefaultSettings = (type: string, item: string) => {
  const data = getSettingsStructure(type);

  return addVersion(
    getStructureDefaultValue(
      `module-${type}-${item}`,
      data.itemsStructure![item],
      data.definitions,
      data.commonStructure
    )
  );
};

export const createModule = (
  parentId: string,
  moduleType: string,
  defaults?: GenericMap<any>,
  isStatic?: boolean
): ModuleEntity => {
  let settings = getModuleDefaultSettings(moduleType);

  if (defaults) {
    settings = merge(settings, defaults);
  }

  const id = generateId();

  return <ModuleEntity>{
    parentId,
    id,
    childrenIds: [],
    settings,
    type: EntityType.Module,
    moduleType: moduleType,
    orientation: "vertical",
    status: Status.None,
    isStatic,
    version: globals.version
  };
};

export const getItemSettingsStructure = (moduleType: string, type: string = "default"): SettingsStructure => {
  const mss = getSettingsStructure(moduleType);
  if (!mss.itemsStructure || !(type in mss.itemsStructure)) {
    throw new Error(`BUG. Module ${moduleType} doesn't have items or ${type} isn't described`);
  }
  return mss.itemsStructure[type];
};
